import { useState, useEffect } from 'react';
import axios from 'axios';
import { Heart, User } from '@phosphor-icons/react';

const API = '/api';

const defaultContent = {
  subtitle: 'Meet the amazing people standing by our side',
  bride: {
    name: 'Stefanie Fernandez',
    title: 'The Bride',
    description: 'A beautiful soul with a heart full of love, ready to begin this new chapter surrounded by family and friends.',
    image: 'https://customer-assets.emergentagent.com/job_coatapeque-wedding/artifacts/z82or38h_Facetune_12-11-2025-12-56-12%202.jpg',
    image_position: '50% 50%',
  },
  groom: {
    name: 'Walter Rodriguez',
    title: 'The Groom',
    description: 'A devoted partner with unwavering love, excited to marry his best friend and soulmate.',
    image: 'https://customer-assets.emergentagent.com/job_coatapeque-wedding/artifacts/0cu3bffc_IMG_4637.jpg',
    image_position: '50% 0%',
  },
  bridesmaids_title: 'The Bridesmaids',
  bridesmaids_subtitle: 'The incredible women supporting Stefanie',
  groomsmen_title: 'The Groomsmen',
  groomsmen_subtitle: 'The amazing men standing with Walter',
  bridesmaids: [],
  groomsmen: [],
  coming_soon_note: '',
};

function PartyMemberCard({ member, side, index }) {
  const bgColor = side === 'bride' ? 'bg-[#FAF8F5]' : 'bg-stone-100';
  const accentColor = side === 'bride' ? 'text-[#C9A86C]' : 'text-[#B8A88A]';
  const borderColor = side === 'bride' ? 'border-[#E8E2D9]' : 'border-stone-200';

  return (
    <div data-testid={`${side === 'bride' ? 'bridesmaid' : 'groomsman'}-card-${index}`} className={`${bgColor} border ${borderColor} p-6 text-center`}>
      <div className={`w-32 h-32 mx-auto mb-4 rounded-full ${side === 'bride' ? 'bg-[#F0EBE3]' : 'bg-stone-200'} flex items-center justify-center overflow-hidden`}>
        {member.image ? (
          <img
            src={member.image}
            alt={member.name}
            className="w-full h-full object-cover"
            style={{ objectPosition: member.image_position || '50% 50%' }}
          />
        ) : (
          <User size={48} weight="light" className={`${accentColor} opacity-50`} />
        )}
      </div>

      <h3 className="font-display text-xl text-stone-900 mb-1">
        {member.name}
      </h3>
      <p className={`font-body text-sm ${accentColor} font-medium uppercase tracking-wide mb-2`}>
        {member.role}
      </p>
      {member.relationship && (
        <p className="font-body text-xs text-stone-500 mb-3">
          {member.relationship}
        </p>
      )}
      {member.description && (
        <p className="font-body text-sm text-stone-600 leading-relaxed">
          {member.description}
        </p>
      )}
    </div>
  );
}

export default function BridalPartyPage() {
  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content/bridal_party`);
        if (response.data && Object.keys(response.data).length > 0) {
          setContent(prev => ({ ...prev, ...response.data }));
        }
      } catch (error) {
        console.log('Using default bridal party content');
      }
    };
    fetchContent();
  }, []);

  const bridesmaids = content.bridesmaids || [];
  const groomsmen = content.groomsmen || [];
  const bride = content.bride || defaultContent.bride;
  const groom = content.groom || defaultContent.groom;

  return (
    <div data-testid="bridal-party-page" className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="page-header bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4">
          <p className="font-body text-sm tracking-[0.2em] uppercase mb-4" style={{ color: "#B8A88A" }}>
            The People We Love
          </p>
          <h1 className="page-title">Bridal Party</h1>
          <p className="page-subtitle">
            {content.subtitle}
          </p>
        </div>
      </div>

      {/* Bride & Groom Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Bride */}
            <div className="text-center" data-testid="bride-card">
              <div className="w-48 h-48 mx-auto mb-6 rounded-full overflow-hidden border-4 border-[#C9A86C]">
                {bride.image ? (
                  <img
                    src={bride.image}
                    alt={bride.name}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: bride.image_position || '50% 50%' }}
                  />
                ) : (
                  <div className="w-full h-full bg-[#F0EBE3] flex items-center justify-center">
                    <User size={64} weight="light" className="text-[#C9A86C] opacity-50" />
                  </div>
                )}
              </div>
              <h2 className="font-display text-3xl text-stone-900 mb-2">
                {bride.name}
              </h2>
              <p className="font-body uppercase tracking-wide text-sm mb-4" style={{ color: '#C9A86C' }}>
                {bride.title || 'The Bride'}
              </p>
              <p className="font-body text-stone-600 leading-relaxed max-w-sm mx-auto">
                {bride.description}
              </p>
            </div>

            {/* Groom */}
            <div className="text-center" data-testid="groom-card">
              <div className="w-48 h-48 mx-auto mb-6 rounded-full overflow-hidden border-4 border-[#C9B99A]">
                {groom.image ? (
                  <img
                    src={groom.image}
                    alt={groom.name}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: groom.image_position || '50% 0%' }}
                  />
                ) : (
                  <div className="w-full h-full bg-stone-100 flex items-center justify-center">
                    <User size={64} weight="light" className="text-[#B8A88A] opacity-50" />
                  </div>
                )}
              </div>
              <h2 className="font-display text-3xl text-stone-900 mb-2">
                {groom.name}
              </h2>
              <p className="font-body text-[#B8A88A] uppercase tracking-wide text-sm mb-4">
                {groom.title || 'The Groom'}
              </p>
              <p className="font-body text-stone-600 leading-relaxed max-w-sm mx-auto">
                {groom.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Heart Divider */}
      <div className="flex justify-center py-8 bg-stone-50">
        <Heart size={32} weight="fill" style={{ color: "#C9B99A" }} />
      </div>

      {/* Bridesmaids Section */}
      {bridesmaids.length > 0 && (
        <section data-testid="bridesmaids-section" className="py-16 bg-stone-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-display text-2xl md:text-3xl text-stone-900 mb-2">
                {content.bridesmaids_title}
              </h2>
              <p className="font-body text-stone-500">
                {content.bridesmaids_subtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {bridesmaids.map((member, index) => (
                <PartyMemberCard key={index} member={member} side="bride" index={index} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Groomsmen Section */}
      {groomsmen.length > 0 && (
        <section data-testid="groomsmen-section" className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-display text-2xl md:text-3xl text-stone-900 mb-2">
                {content.groomsmen_title}
              </h2>
              <p className="font-body text-stone-500">
                {content.groomsmen_subtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {groomsmen.map((member, index) => (
                <PartyMemberCard key={index} member={member} side="groom" index={index} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Optional Note */}
      {content.coming_soon_note && (
        <section className="py-12 bg-[#F5F2ED] border-y border-[#E8E2D9]">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="font-body text-stone-600 text-sm">
              {content.coming_soon_note}
            </p>
          </div>
        </section>
      )}
    </div>
  );
}
